// src/context/SocketContext.tsx
import { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { showToast } from '../utils/showToast';

export interface AppNotification {
  id: string;
  message: string;
  type: 'user_registered' | 'course_updated' | 'blog_posted' | 'enrollment_status';
  link?: string;
  read: boolean;
  timestamp: Date;
}

interface SocketContextType {
  notifications: AppNotification[];
  clearNotifications: () => void;
  markAllAsRead: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  // Helper to create a notification
  const addNotification = (idPrefix: string, message: string, link: string, type: AppNotification['type']) => {
    const newNotif: AppNotification = {
      id: `${idPrefix}-${Date.now()}`,
      message,
      type,
      link,
      read: false,
      timestamp: new Date(),
    };
    setNotifications(prev => [newNotif, ...prev]);
    showToast(newNotif.message, { type: 'info' });
  };

  useEffect(() => {
    if (!user) return;

    const BACKEND_URL = import.meta.env.VITE_API || 'http://localhost:5000';
    console.log(`[Socket.IO] Attempting to connect to ${BACKEND_URL}`);

    const socket: Socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      console.log('✅ [Socket.IO] Connected successfully. Socket ID:', socket.id);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ [Socket.IO] Connection Error:', error.message);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ [Socket.IO] Disconnected. Reason:', reason);
    });

    // --- Notification Event Listeners ---
    socket.on('newUserRegistered', (data: { userId: string; name: string; email: string }) => {
      if (user.role === 'mentor' || user.isAdmin) {
        addNotification(
          'user',
          `New user "${data.name}" registered and needs approval.`,
          '/dashboard/mentor/approvals',
          'user_registered'
        );
      }
    });

    socket.on('courseUpdated', (data: { courseId: string; title: string; message: string }) => {
      if (user.role === 'student') {
        addNotification(
          'course',
          `Course "${data.title}" has updates: ${data.message}`,
          `/courses/${data.courseId}`,
          'course_updated'
        );
      }
    });

    socket.on('newBlogPosted', (data: { blogId: string; title: string; authorName: string; slug: string }) => {
      addNotification(
        'blog',
        `New blog post: "${data.title}" by ${data.authorName}.`,
        `/blogs/${data.slug}`,
        'blog_posted'
      );
    });

    socket.on('enrollmentStatusUpdated', (data: { enrollmentId: string; studentId: string; courseId: string; newStatus: string; message: string }) => {
      if (user._id === data.studentId) {
        addNotification(
          'enroll-status',
          `Your course status updated: ${data.message}`,
          '/dashboard/student/my-courses',
          'enrollment_status'
        );
      }
    });

    socket.on('enrollmentCreated', (data: { enrollmentId: string; studentId: string; courseId: string; completionStatus: string; message: string }) => {
      if (user.role === 'mentor' || user.isAdmin) {
        addNotification(
          'enroll-created',
          data.message,
          '/dashboard/mentor/manage-enrollments',
          'enrollment_status'
        );
      }
    });

    return () => {
      console.log('[Socket.IO] Cleaning up connection...');
      socket.disconnect();
    };
  }, [user]);

  const clearNotifications = () => setNotifications([]);
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  return (
    <SocketContext.Provider value={{ notifications, clearNotifications, markAllAsRead }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};